import { useMemo, useState, useCallback, useEffect, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import supabase from '@/supabaseClient'
import { logFeatureUsage } from '@/utils/logger'
import { useRecommendationQuota } from '@/pages/Explore/useRecommendationQuota'

type BacklogEntry = {
  game_id: string
  status: string
  games: {
    id: string
    title: string
    background_image?: string
    igdb_id?: string | number
    game_genres?: { genres: { name: string } }[]
  }
}

type Suggestion = {
  id: string
  title: string
  note?: string
  status?: string
  image?: string
  genres?: string[]
}

const normalizeTitle = (value: string) =>
  value.toLowerCase().replace(/[^a-z0-9]/gi, '').trim()

type PurchaseIntentPageProps = {
  isDevUser?: boolean
}

const PurchaseIntentPage = ({ isDevUser = false }: PurchaseIntentPageProps) => {
  const navigate = useNavigate()
  const location = useLocation()
  const { isDevUser: isDevUserFromHook, loading: quotaLoading } = useRecommendationQuota()
  const effectiveIsDevUser = isDevUser || isDevUserFromHook
  const [intent, setIntent] = useState((location.state as { intent?: string })?.intent || '')
  const [submittedIntent, setSubmittedIntent] = useState<string | null>(null)
  const [userId, setUserId] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [suggestions, setSuggestions] = useState<Suggestion[]>([])
  const [backlogSize, setBacklogSize] = useState<number>(0)
  const [actionBusy, setActionBusy] = useState<Record<string, boolean>>({})
  const [intentWhy, setIntentWhy] = useState((location.state as { intentWhy?: string })?.intentWhy || '')
  const [targetCover, setTargetCover] = useState<string | null>(null)
  const [targetTitle, setTargetTitle] = useState<string | null>(null)
  const [shareRowId, setShareRowId] = useState<string | null>(null)
  const [shareUrl, setShareUrl] = useState<string | null>(null)
  const [shareLoading, setShareLoading] = useState(false)
  const autoSubmitted = useRef(false)

  const headline = useMemo(() => {
    if (submittedIntent) {
      return `Backlog Buddy picks for ${submittedIntent}`
    }
    return 'Before you buy that —\ncheck what you already own.'
  }, [submittedIntent])

  const parseRecommendation = useCallback(
    (text: string, backlog: BacklogEntry[]) => {
      const numberedLines = text
        .split('\n')
        .filter((line) => /^\d+\./.test(line.trim()))

      const parsed = numberedLines
        .map((line) => {
          const boldMatch =
            line.match(/^\d+\.\s+\*\*(.+?)\*\*\s*[-:]?\s*(.*)/) ||
            line.match(/^\d+\.\s+(.+?)\s*[-:]?\s*(.*)/)
          if (!boldMatch) return null

          const [, titleRaw, noteRaw] = boldMatch
          const normalized = normalizeTitle(titleRaw)
          const matchedGame = backlog.find((entry) => {
            const backlogTitle = entry.games?.title || ''
            const normalizedBacklog = normalizeTitle(backlogTitle)
            return (
              normalizedBacklog.includes(normalized) ||
              normalized.includes(normalizedBacklog)
            )
          })

          if (!matchedGame) return null

          return {
            id: matchedGame.game_id,
            title: matchedGame.games?.title || titleRaw.trim(),
            note: noteRaw?.trim() || 'In your backlog and fits the vibe.',
            status: matchedGame.status,
            image: matchedGame.games?.background_image,
            genres:
              matchedGame.games?.game_genres
                ?.map((g) => g.genres.name)
                .filter(Boolean) || [],
          }
        })
        .filter(Boolean) as Suggestion[]

      return parsed
    },
    []
  )

  const fetchRecommendations = useCallback(
    async (consideringGame: string) => {
      try {
        setIsLoading(true)
        setError(null)
        setSuggestions([])
        setTargetCover(null)
        setTargetTitle(null)

        const {
          data: { user },
          error: userError,
        } = await supabase.auth.getUser()

        if (userError || !user) {
          setError('You need to be signed in to get matches from your backlog.')
          return
        }
        setUserId(user.id)

        const { data: userGames, error: backlogError } = await supabase
          .from('user_games')
          .select(
            `
            game_id,
            status,
            games (
              id,
              title,
              background_image,
              igdb_id,
              game_genres (
                genres ( name )
              )
            )
          `
          )
          .eq('user_id', user.id)
          .not('status', 'in.("Done","Endless","Satisfied","Currently Playing")')

        if (backlogError) {
          console.error('Error loading backlog for purchase intent:', backlogError)
          setError('Something went wrong. Give it another try — and if it keeps happening, let me know.')
          return
        }

        const backlog = (userGames as BacklogEntry[]) || []
        setBacklogSize(backlog.length)

        if (backlog.length === 0) {
          setError('Add a few games to your library first — then Backlog Buddy has something to work with.')
          return
        }

        const displayName =
          (user.user_metadata as Record<string, unknown>)?.username ||
          (user.user_metadata as Record<string, unknown>)?.full_name ||
          user.email ||
          'you'

        const { data: { session } } = await supabase.auth.getSession()

        const payload = {
          mode: 'purchase_alternative',
          consideringGame,
          backlog: backlog.map((entry) => ({
            title: entry.games?.title || '',
            igdb_id: entry.games?.igdb_id,
            background_image: entry.games?.background_image || null,
            status: entry.status,
            genre:
              entry.games?.game_genres
                ?.map((g) => g.genres.name)
                .filter(Boolean)
                .join(', ') || '',
            playStyle: '',
            playtime: '',
          })),
          intentWhy: intentWhy.trim() || null,
          username: displayName,
        }

        const response = await fetch(
          `${import.meta.env.VITE_API_URL}/api/openai/recommend`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${session?.access_token}`,
            },
            body: JSON.stringify(payload),
          }
        )

        const result = await response.json()

        if (response.status === 429) {
          setError(
            result?.error ||
              "You've hit today's limit. Try again tomorrow."
          )
          return
        }

        if (!response.ok) {
          const apiError =
            typeof result?.error === 'string'
              ? result.error
              : 'Something went wrong. Give it another try.'
          setError(apiError)
          return
        }

        const recommendationText =
          typeof result?.recommendation === 'string'
            ? result.recommendation
            : ''

        setTargetCover(typeof result?.targetCover === 'string' ? result.targetCover : null)
        setTargetTitle(typeof result?.targetTitle === 'string' ? result.targetTitle : submittedIntent)
        setShareRowId(result.rowId || null)
        setShareUrl(null)

        const parsed = parseRecommendation(recommendationText, backlog)
        setSuggestions(parsed)

        logFeatureUsage({
          feature: 'purchase_intent',
          metadata: {
            consideringGame,
            backlogSize: backlog.length,
            suggestionCount: parsed.length,
            isDevUser: effectiveIsDevUser,
          },
        })
      } catch (e) {
        console.error('Purchase intent flow failed:', e)
        setError('Something went wrong. Give it another try.')
      } finally {
        setIsLoading(false)
      }
    },
    [parseRecommendation, intentWhy, effectiveIsDevUser]
  )

  useEffect(() => {
    if (autoSubmitted.current) return
    if (quotaLoading) return
    const prefilledIntent = (location.state as { intent?: string })?.intent?.trim()
    if (prefilledIntent) {
      autoSubmitted.current = true
      setSubmittedIntent(prefilledIntent)
      fetchRecommendations(prefilledIntent)
    }
  }, [fetchRecommendations, location.state, quotaLoading])

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const trimmed = intent.trim()
    if (!trimmed) return
    setSubmittedIntent(trimmed)
    fetchRecommendations(trimmed)
  }

  const updateStatus = async (gameId: string, status: string) => {
    if (!userId) {
      setError('Please log in again to update your library.')
      return
    }
    setActionBusy((prev) => ({ ...prev, [gameId]: true }))
    const { error: updateError } = await supabase
      .from('user_games')
      .update({ status })
      .eq('game_id', gameId)
      .eq('user_id', userId)

    if (updateError) {
      console.error('Failed to update status:', updateError)
      setError('Something went wrong. Give it another try.')
    } else {
      setSuggestions((prev) =>
        prev.map((s) =>
          s.id === gameId ? { ...s, status } : s
        )
      )
    }
    setActionBusy((prev) => ({ ...prev, [gameId]: false }))
  }

  const handleShare = async () => {
    if (!shareRowId) return
    setShareLoading(true)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const res = await fetch(`${import.meta.env.VITE_API_URL}/api/shares`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify({ rowId: shareRowId }),
      })
      const json = await res.json()
      const url = `${window.location.origin}/share/${json.shareId}`
      setShareUrl(url)
      navigator.clipboard.writeText(url)
    } catch (e) {
      console.error('Share failed:', e)
    } finally {
      setShareLoading(false)
    }
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex flex-col gap-6 max-w-5xl">
        <div className="flex flex-col gap-2">
          <p className="text-sm uppercase tracking-wide text-primary font-semibold">Backlog Buddy</p>
          <h1 className="text-4xl font-bold font-serif" style={{ whiteSpace: 'pre-line' }}>{headline}</h1>
          {!submittedIntent && (
            <p className="text-base-content/70">
              Tell it what you're about to buy. It finds what you already own that scratches the same itch.
            </p>
          )}
        </div>

        <form onSubmit={handleSubmit} className="card bg-base-100 shadow-md">
          <div className="card-body gap-4">
            <div className="form-control">
              <label className="label" htmlFor="intent-input">
                <span className="label-text">I want to buy…</span>
              </label>
              <input
                id="intent-input"
                type="text"
                placeholder="What are you about to buy?"
                className="input input-bordered w-full"
                value={intent}
                onChange={(event) => setIntent(event.target.value)}
              />
            </div>
            <div className="form-control">
              <label className="label">
                <span className="label-text">What's pulling you toward it?</span>
                <span className="label-text-alt text-xs text-base-content/70">
                  More specific = better matches
                </span>
              </label>
              <textarea
                className="textarea textarea-bordered"
                placeholder="The vibe, the look, something a friend mentioned..."
                value={intentWhy}
                onChange={(event) => setIntentWhy(event.target.value)}
              />
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <button
                type="submit"
                className="btn btn-primary"
                disabled={!intent.trim() || isLoading}
              >
                {isLoading ? `Hang on, looking through your ${backlogSize || ''} games… you might be surprised what's in there.` : 'Find a match'}
              </button>
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => navigate('/app/library')}
              >
                Go to my games
              </button>
            </div>
          </div>
        </form>

        {submittedIntent && (
          <div className="card bg-base-100 shadow-md">
            <div className="card-body">
              <div className="flex items-start gap-4">
                {targetCover && (
                  <img
                    src={targetCover}
                    alt={targetTitle || submittedIntent}
                    className="w-14 h-20 object-cover rounded-lg shrink-0"
                  />
                )}
                <div>
                  <p className="text-xs uppercase tracking-wide text-primary">Considering</p>
                  <h2 className="card-title">{targetTitle || submittedIntent}</h2>
                  <p className="text-sm text-base-content/70">
                    {isLoading
                      ? 'Looking through your backlog…'
                      : "Let's see what you already own that scratches the same itch."}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {error && (
          <div className="alert alert-error">
            <span>{error}</span>
          </div>
        )}

        {submittedIntent && !isLoading && !error && suggestions.length === 0 && (
          <div className="alert">
            <span>
              Honestly? Nothing in your library quite fits. This might actually be worth buying.
            </span>
          </div>
        )}

        <section
          aria-label="Backlog suggestions"
          className="grid gap-4 lg:grid-cols-3"
        >
          {isLoading && (
            <div className="col-span-full flex flex-col items-center py-8 gap-3">
              <div className="loading loading-spinner loading-lg"></div>
              <p className="text-sm text-base-content/60">
                Looking through your {backlogSize > 0 ? `${backlogSize} ` : ''}games… you might be surprised what's in there.
              </p>
            </div>
          )}

          {!isLoading &&
            suggestions.map((suggestion) => (
              <article
                key={suggestion.id}
                className="card bg-base-100 shadow-md h-full"
              >
                {suggestion.image && (
                  <figure>
                    <img
                      src={suggestion.image}
                      alt={suggestion.title}
                      className="w-full h-40 object-cover rounded-t-xl"
                    />
                  </figure>
                )}
                <div className="card-body gap-3">
                  <div className="flex flex-col gap-1">
                    <h2 className="card-title leading-tight">{suggestion.title}</h2>
                    {suggestion.genres?.length ? (
                      <p className="text-sm text-base-content/70">
                        {suggestion.genres.join(' • ')}
                      </p>
                    ) : null}
                    {suggestion.status && (
                      <div className="badge badge-outline badge-sm">
                        Status: {suggestion.status}
                      </div>
                    )}
                  </div>
                  <p className="text-sm">{suggestion.note}</p>
                  <div className="flex items-center gap-2">
                    <button
                      className="btn btn-primary btn-sm"
                      type="button"
                      disabled={actionBusy[suggestion.id]}
                      onClick={() => updateStatus(suggestion.id, 'Currently Playing')}
                    >
                      {actionBusy[suggestion.id] ? 'Updating…' : 'Start playing'}
                    </button>
                    <button
                      className="btn btn-outline btn-sm"
                      type="button"
                      onClick={() => navigate(`/app/game/${suggestion.id}`)}
                    >
                      View details
                    </button>
                  </div>
                </div>
              </article>
            ))}
        </section>

        {!isLoading && suggestions.length > 0 && shareRowId && (
          <div className="card bg-base-100 shadow-sm border border-base-200">
            <div className="card-body py-5 gap-3">
              <div>
                <p className="font-serif italic" style={{ color: '#3d2a1e', fontWeight: 600 }}>Okay, I already own some great games!</p>
                <p className="text-sm mt-0.5" style={{ color: '#6a5040' }}>Share your Backlog Buddy picks as a card.</p>
              </div>
              {shareUrl ? (
                <div className="flex items-center gap-2">
                  <div className="flex-1 flex items-center gap-2 bg-base-200 rounded-lg px-3 py-2 min-w-0">
                    <svg className="shrink-0 text-success" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                    <a href={shareUrl} target="_blank" rel="noreferrer" className="text-sm text-base-content/70 truncate hover:text-primary">
                      {shareUrl}
                    </a>
                  </div>
                  <button
                    type="button"
                    className="btn btn-sm btn-ghost shrink-0"
                    onClick={() => navigator.clipboard.writeText(shareUrl)}
                  >
                    Copy
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  className="btn btn-primary w-fit gap-2"
                  onClick={handleShare}
                  disabled={shareLoading}
                >
                  {shareLoading ? (
                    <><span className="loading loading-spinner loading-xs" />Creating link…</>
                  ) : (
                    <>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/>
                        <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
                      </svg>
                      Share these picks
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default PurchaseIntentPage
